import type { NavSchema } from '../../eum-navigation-builder/eum-navigation-builder.js';

class NavigationService {
    private static instance: NavigationService;
    private bearerToken: string | null = null;
    private endpoint = '/api/Portal/NavigationJSON';

    static getInstance(): NavigationService {
        if (!NavigationService.instance) {
            NavigationService.instance = new NavigationService();
        }
        return NavigationService.instance;
    }

    setToken(token: string): void {
        this.bearerToken = token;
    }

    setEndpoint(url: string): void {
        if (url) this.endpoint = url;
    }

    async get(): Promise<NavSchema> {
        const headers: Record<string, string> = { 'Accept': 'application/json' };
        if (this.bearerToken) headers['Authorization'] = this.bearerToken;

        const response = await fetch(this.endpoint, { method: 'GET', headers });
        if (response.status === 404) return [];
        if (!response.ok) throw new Error(`Navigation GET failed: ${response.status}`);

        const text = await response.text();
        if (!text) return [];
        let parsed: any = JSON.parse(text);
        // API may double- (or triple-) encode the schema as a JSON string.
        // Keep unwrapping while it's still a string that looks like JSON.
        let guard = 5;
        while (typeof parsed === 'string' && guard-- > 0) {
            const trimmed = parsed.trim();
            if (!trimmed) return [];
            try { parsed = JSON.parse(trimmed); } catch { break; }
        }
        // If we got an object wrapper, pull the first array-ish property out.
        if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
            for (const key of ['navigation', 'value', 'data', 'items', 'schema']) {
                const v = parsed[key];
                if (typeof v === 'string') {
                    try { parsed = JSON.parse(v); break; } catch { /* ignore */ }
                } else if (Array.isArray(v)) {
                    parsed = v; break;
                }
            }
        }
        if (!Array.isArray(parsed)) {
            console.warn('[navigation-service] Unexpected response shape:', parsed);
            return [];
        }
        return parsed as NavSchema;
    }

    async save(schema: NavSchema): Promise<void> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json; charset=utf-8',
            'Accept': 'application/json',
        };
        if (this.bearerToken) headers['Authorization'] = this.bearerToken;

        const response = await fetch(this.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(schema),
        });

        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(`Navigation POST failed: ${response.status} ${body}`.trim());
        }
    }
}

export const navigationService = NavigationService.getInstance();
